import React, { useState, useEffect, useCallback } from 'react';
import { payPeriodAPI, manualEntryAPI, payrollSettingsAPI } from '../services/api';
import type { PayPeriod, PayrollManualEntry, PayrollSettings } from '../types';

interface EmployeeSummary {
  name: string;
  rate: string | null;
  combined_regular_hours: string;
  combined_regular_pay: string;
  ot_hours: string;
  ot_rate: string | null;
  ot_pay: string;
  dt_hours: string;
  dt_rate: string | null;
  dt_pay: string;
  total_hours: string;
  total_pay: string;
  rate_segment?: string;
}

interface GrandTotals {
  total_hours: string;
  total_pay: string;
}

interface ManualEntryPreview {
  id: number;
  name: string;
  description: string;
  amount: string;
  sort_order: number;
}

interface TemplateFields {
  FIRM_NAME: string;
  FIRM_ADDRESS: string;
  FIRM_PHONE: string;
  FIRM_FAX: string;
  DELIVERY: string;
  RECIPIENT: string[];
  ATTENTION: string;
  SALUTATION: string;
  CLOSING: string;
  SIGNOFF: string;
  SIGNER_NAME: string;
}

interface PayrollPreviewData {
  period_start: string;
  period_end: string;
  employees: EmployeeSummary[];
  manual_entries?: ManualEntryPreview[];
  grand_totals: GrandTotals;
  signature_data_url?: string;
  template_fields?: TemplateFields;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateLong(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function fmtMoney(val: string): string {
  return parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function buildLine(label: string, hours: string, rate: string | null, pay: string): string {
  const rateStr = rate ? `$${fmtMoney(rate)}` : '$0.00';
  return `${label} - ${hours} hours @ ${rateStr}/hour = $${fmtMoney(pay)} gross ${label.toLowerCase()} pay`;
}

const PayrollReport: React.FC = () => {
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<PayrollPreviewData | null>(null);
  const [previewPeriodId, setPreviewPeriodId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [manualPeriodId, setManualPeriodId] = useState<number | null>(null);
  const [manualEntries, setManualEntries] = useState<PayrollManualEntry[]>([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PayrollManualEntry | null>(null);
  const [entryForm, setEntryForm] = useState({ name: '', description: '', amount: '' });
  const [lockingEnabled, setLockingEnabled] = useState(true);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [letterSettings, setLetterSettings] = useState({
    letter_firm_name: '', letter_firm_address: '', letter_firm_phone: '', letter_firm_fax: '',
    letter_acct_email: '', letter_acct_name: '', letter_acct_address: '', letter_acct_contact: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [periodsRes, settingsRes] = await Promise.all([
          payPeriodAPI.getAll({ page_size: 50 }),
          payrollSettingsAPI.get().catch(() => null),
        ]);
        setPeriods(periodsRes.data.results);
        if (settingsRes?.data) {
          setLockingEnabled(settingsRes.data.pay_period_locking_enabled !== false);
          const s = settingsRes.data as PayrollSettings;
          setLetterSettings({
            letter_firm_name: s.letter_firm_name || '',
            letter_firm_address: s.letter_firm_address || '',
            letter_firm_phone: s.letter_firm_phone || '',
            letter_firm_fax: s.letter_firm_fax || '',
            letter_acct_email: s.letter_acct_email || '',
            letter_acct_name: s.letter_acct_name || '',
            letter_acct_address: s.letter_acct_address || '',
            letter_acct_contact: s.letter_acct_contact || '',
          });
        }
      } catch (err) {
        console.error('Failed to fetch pay periods:', err);
        setError('Failed to load pay periods.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setError('');
    try {
      await payrollSettingsAPI.update(letterSettings);
      setSuccess('Letter settings saved.');
    } catch {
      setError('Failed to save letter settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const [downloadDropdown, setDownloadDropdown] = useState<number | null>(null);

  const handleDownload = async (periodId: number, format: 'pdf' | 'docx' = 'pdf') => {
    setDownloading(periodId);
    setDownloadDropdown(null);
    setError('');
    setSuccess('');
    try {
      if (format === 'docx') {
        await payPeriodAPI.downloadPayrollReportDocx(periodId);
      } else {
        await payPeriodAPI.downloadPayrollReport(periodId);
      }
      setSuccess('Report downloaded successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate report.');
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = async (periodId: number) => {
    if (previewPeriodId === periodId) {
      setPreviewData(null);
      setPreviewPeriodId(null);
      return;
    }
    setPreviewing(periodId);
    setError('');
    setSuccess('');
    try {
      const response = await payPeriodAPI.getPayrollPreview(periodId);
      setPreviewData(response.data);
      setPreviewPeriodId(periodId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load preview.');
    } finally {
      setPreviewing(null);
    }
  };

  const fetchManualEntries = useCallback(async (periodId: number) => {
    setManualLoading(true);
    try {
      const response = await manualEntryAPI.getAll({ pay_period: periodId });
      setManualEntries(response.data.results);
    } catch {
      setError('Failed to load manual entries.');
    } finally {
      setManualLoading(false);
    }
  }, []);

  const toggleManualEntries = async (periodId: number) => {
    if (manualPeriodId === periodId) {
      setManualPeriodId(null);
      setManualEntries([]);
      setEditingEntry(null);
      setEntryForm({ name: '', description: '', amount: '' });
      return;
    }
    setManualPeriodId(periodId);
    setEditingEntry(null);
    setEntryForm({ name: '', description: '', amount: '' });
    await fetchManualEntries(periodId);
  };

  const handleSaveEntry = async () => {
    if (!manualPeriodId || !entryForm.name || !entryForm.description || !entryForm.amount) return;
    setError('');
    try {
      if (editingEntry?.id) {
        await manualEntryAPI.update(editingEntry.id, {
          name: entryForm.name,
          description: entryForm.description,
          amount: entryForm.amount,
        });
      } else {
        await manualEntryAPI.create({
          pay_period: manualPeriodId,
          name: entryForm.name,
          description: entryForm.description,
          amount: entryForm.amount,
          sort_order: manualEntries.length,
        });
      }
      setEditingEntry(null);
      setEntryForm({ name: '', description: '', amount: '' });
      await fetchManualEntries(manualPeriodId);
      if (previewPeriodId === manualPeriodId) {
        const response = await payPeriodAPI.getPayrollPreview(manualPeriodId);
        setPreviewData(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save manual entry.');
    }
  };

  const handleDeleteEntry = async (entryId: number) => {
    if (!manualPeriodId) return;
    setError('');
    try {
      await manualEntryAPI.delete(entryId);
      await fetchManualEntries(manualPeriodId);
      if (previewPeriodId === manualPeriodId) {
        const response = await payPeriodAPI.getPayrollPreview(manualPeriodId);
        setPreviewData(response.data);
      }
    } catch {
      setError('Failed to delete manual entry.');
    }
  };

  const startEditEntry = (entry: PayrollManualEntry) => {
    setEditingEntry(entry);
    setEntryForm({ name: entry.name, description: entry.description, amount: entry.amount });
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setEntryForm({ name: '', description: '', amount: '' });
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="container">
      <div className="card">
        <h2>Payroll Reports</h2>
        <p style={{ color: '#7f8c8d', marginBottom: '1.5rem' }}>
          Preview payroll data or download the letter for each pay period.
        </p>

        <div style={{ marginBottom: '1.5rem' }}>
          <button
            className="btn btn-small btn-secondary"
            onClick={() => setSettingsOpen(!settingsOpen)}
          >
            Letter Settings {settingsOpen ? '▲' : '▼'}
          </button>
          {settingsOpen && (
            <div style={{
              marginTop: '0.75rem', padding: '1.5rem', backgroundColor: '#f8f9fa',
              border: '1px solid #ddd', borderRadius: '8px',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Law Firm Name</label>
                  <input type="text" value={letterSettings.letter_firm_name}
                    onChange={e => setLetterSettings({ ...letterSettings, letter_firm_name: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Law Firm Address</label>
                  <input type="text" value={letterSettings.letter_firm_address}
                    onChange={e => setLetterSettings({ ...letterSettings, letter_firm_address: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Law Firm Phone</label>
                  <input type="text" value={letterSettings.letter_firm_phone}
                    onChange={e => setLetterSettings({ ...letterSettings, letter_firm_phone: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Law Firm Fax</label>
                  <input type="text" value={letterSettings.letter_firm_fax}
                    onChange={e => setLetterSettings({ ...letterSettings, letter_firm_fax: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Accounting Firm Email</label>
                  <input type="text" value={letterSettings.letter_acct_email}
                    onChange={e => setLetterSettings({ ...letterSettings, letter_acct_email: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Accounting Firm Name</label>
                  <input type="text" value={letterSettings.letter_acct_name}
                    onChange={e => setLetterSettings({ ...letterSettings, letter_acct_name: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Accounting Firm Address</label>
                  <textarea rows={2} value={letterSettings.letter_acct_address}
                    onChange={e => setLetterSettings({ ...letterSettings, letter_acct_address: e.target.value })}
                    style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px' }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Accounting Firm Contact</label>
                  <input type="text" value={letterSettings.letter_acct_contact}
                    onChange={e => setLetterSettings({ ...letterSettings, letter_acct_contact: e.target.value })} />
                </div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button className="btn btn-primary" onClick={handleSaveSettings} disabled={savingSettings}>
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        {periods.length === 0 ? (
          <p style={{ color: '#95a5a6' }}>
            No pay periods found. Configure pay periods first in Pay Period Management.
          </p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Pay Period</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  {lockingEnabled && <th>Status</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {periods.map(period => (
                  <React.Fragment key={period.id}>
                    <tr>
                      <td style={{ fontWeight: period.is_current ? 'bold' : 'normal' }}>
                        {period.name || `${period.start_date} – ${period.end_date}`}
                        {period.is_current && (
                          <span style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.75rem',
                            backgroundColor: '#3498db',
                            color: 'white',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '10px',
                          }}>
                            Current
                          </span>
                        )}
                      </td>
                      <td>{formatDate(period.start_date)}</td>
                      <td>{formatDate(period.end_date)}</td>
                      {lockingEnabled && (
                        <td>
                          {period.is_locked ? (
                            <span className="status-badge status-paid">Locked</span>
                          ) : (
                            <span className="status-badge status-draft">Open</span>
                          )}
                        </td>
                      )}
                      <td className="actions">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => toggleManualEntries(period.id!)}
                          style={{ marginRight: '0.25rem' }}
                        >
                          {manualPeriodId === period.id ? 'Hide Entries' : 'Manual Entries'}
                        </button>
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handlePreview(period.id!)}
                          disabled={previewing === period.id}
                        >
                          {previewing === period.id
                            ? 'Loading...'
                            : previewPeriodId === period.id
                              ? 'Hide Preview'
                              : 'Preview'}
                        </button>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <button
                            className="btn btn-small btn-primary"
                            onClick={() => setDownloadDropdown(downloadDropdown === period.id ? null : period.id!)}
                            disabled={downloading === period.id}
                          >
                            {downloading === period.id ? 'Generating...' : 'Download ▾'}
                          </button>
                          {downloadDropdown === period.id && (
                            <div style={{
                              position: 'absolute', right: 0, top: '100%', zIndex: 10,
                              backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)', minWidth: '120px',
                            }}>
                              <button
                                style={{ display: 'block', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                                onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                                onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                                onClick={() => handleDownload(period.id!, 'pdf')}
                              >PDF</button>
                              <button
                                style={{ display: 'block', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                                onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                                onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                                onClick={() => handleDownload(period.id!, 'docx')}
                              >Word</button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    {manualPeriodId === period.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: '1rem', borderTop: 'none', backgroundColor: '#f8f9fa' }}>
                          <ManualEntryManager
                            entries={manualEntries}
                            loading={manualLoading}
                            form={entryForm}
                            editing={editingEntry}
                            onFormChange={setEntryForm}
                            onSave={handleSaveEntry}
                            onEdit={startEditEntry}
                            onDelete={handleDeleteEntry}
                            onCancel={cancelEdit}
                          />
                        </td>
                      </tr>
                    )}
                    {previewPeriodId === period.id && previewData && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0, borderTop: 'none' }}>
                          <PayrollPreview
                            data={previewData}
                            onDownload={(fmt) => handleDownload(period.id!, fmt)}
                            downloading={downloading === period.id}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


const ManualEntryManager: React.FC<{
  entries: PayrollManualEntry[];
  loading: boolean;
  form: { name: string; description: string; amount: string };
  editing: PayrollManualEntry | null;
  onFormChange: (form: { name: string; description: string; amount: string }) => void;
  onSave: () => void;
  onEdit: (entry: PayrollManualEntry) => void;
  onDelete: (id: number) => void;
  onCancel: () => void;
}> = ({ entries, loading, form, editing, onFormChange, onSave, onEdit, onDelete, onCancel }) => {
  if (loading) return <div>Loading manual entries...</div>;

  return (
    <div>
      <h4 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Manual Entries</h4>
      <p style={{ color: '#7f8c8d', fontSize: '0.85rem', marginBottom: '1rem' }}>
        Add salaried employee pay or other line items that appear in the payroll letter.
      </p>

      {entries.length > 0 && (
        <table style={{ width: '100%', marginBottom: '1rem', fontSize: '0.9rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '0.4rem' }}>Name</th>
              <th style={{ textAlign: 'left', padding: '0.4rem' }}>Description</th>
              <th style={{ textAlign: 'right', padding: '0.4rem' }}>Amount</th>
              <th style={{ textAlign: 'right', padding: '0.4rem', width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => (
              <tr key={entry.id}>
                <td style={{ padding: '0.4rem' }}>{entry.name}</td>
                <td style={{ padding: '0.4rem' }}>{entry.description}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>${fmtMoney(entry.amount)}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                  <button className="btn btn-small btn-secondary" onClick={() => onEdit(entry)}
                    style={{ marginRight: '0.25rem' }}>Edit</button>
                  <button className="btn btn-small btn-danger" onClick={() => onDelete(entry.id!)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.2rem' }}>Name</label>
          <input
            type="text"
            value={form.name}
            onChange={e => onFormChange({ ...form, name: e.target.value })}
            placeholder="e.g. Mark Wolters"
            style={{ padding: '0.4rem', width: '160px' }}
          />
        </div>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.2rem' }}>Description</label>
          <input
            type="text"
            value={form.description}
            onChange={e => onFormChange({ ...form, description: e.target.value })}
            placeholder="e.g. Salary for the Month of March"
            style={{ padding: '0.4rem', width: '100%' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.2rem' }}>Amount ($)</label>
          <input
            type="number"
            step="0.01"
            value={form.amount}
            onChange={e => onFormChange({ ...form, amount: e.target.value })}
            placeholder="0.00"
            style={{ padding: '0.4rem', width: '120px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            className="btn btn-small btn-primary"
            onClick={onSave}
            disabled={!form.name || !form.description || !form.amount}
          >
            {editing ? 'Update' : 'Add'}
          </button>
          {editing && (
            <button className="btn btn-small btn-secondary" onClick={onCancel}>Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
};


const PayrollPreview: React.FC<{
  data: PayrollPreviewData;
  onDownload: (format: 'pdf' | 'docx') => void;
  downloading: boolean;
}> = ({ data, onDownload, downloading }) => {
  const [showDlDropdown, setShowDlDropdown] = useState(false);
  const periodEndFormatted = formatDateLong(data.period_end);
  const tpl = data.template_fields;

  const firmName = tpl?.FIRM_NAME ?? 'NATIONAL CHOICE LAWYERS';
  const firmAddress = tpl?.FIRM_ADDRESS ?? '19727 VENTURA BLVD, SUITE B WOODLAND HILLS, CA 91364';
  const firmPhone = tpl?.FIRM_PHONE ?? '(818) 888-1881';
  const firmFax = tpl?.FIRM_FAX ?? '(818) 888-1882';
  const delivery = tpl?.DELIVERY ?? 'Via e-mail only: accountants@socal.rr.com';
  const recipient = tpl?.RECIPIENT ?? ['AARON BARLEV & CO.', '9542 Topanga Canyon Blvd.', 'Chatsworth, CA 91311'];
  const attention = tpl?.ATTENTION ?? 'Christina Pagulayan';
  const salutation = tpl?.SALUTATION ?? 'Dear Christina:';
  const closing = tpl?.CLOSING ?? 'Please let me know if there is any information you require.';
  const signoff = tpl?.SIGNOFF ?? 'Very truly yours,';
  const signerName = tpl?.SIGNER_NAME ?? 'Koorosh K. Shahrokh, Esq.';

  return (
    <div style={{
      padding: '2rem',
      backgroundColor: '#fafafa',
      borderTop: '2px solid #3498db',
      fontFamily: 'Georgia, "Times New Roman", serif',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div style={{ color: '#7f8c8d', fontFamily: 'inherit', fontSize: '0.85rem', fontStyle: 'italic' }}>
          Preview of payroll letter
        </div>
        <div style={{ position: 'relative', display: 'inline-block', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
          <button className="btn btn-primary" onClick={() => setShowDlDropdown(!showDlDropdown)} disabled={downloading}>
            {downloading ? 'Generating...' : 'Download ▾'}
          </button>
          {showDlDropdown && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', zIndex: 10,
              backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)', minWidth: '120px',
            }}>
              <button
                style={{ display: 'block', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => { setShowDlDropdown(false); onDownload('pdf'); }}
              >PDF</button>
              <button
                style={{ display: 'block', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => { setShowDlDropdown(false); onDownload('docx'); }}
              >Word</button>
            </div>
          )}
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '4px',
        padding: '2.5rem 2rem',
        maxWidth: '680px',
        margin: '0 auto',
        lineHeight: '1.6',
        fontSize: '0.95rem',
      }}>
        {/* Letterhead */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '0.1em',
            fontFamily: '"Copperplate Gothic Light", "Copperplate", "Small Caps", serif',
          }}>
            {firmName}
          </div>
          <div style={{
            fontSize: '0.75rem', color: '#555', letterSpacing: '0.05em',
            fontFamily: '"Copperplate Gothic Light", "Copperplate", "Small Caps", serif',
          }}>
            {firmAddress}
          </div>
          <div style={{
            fontSize: '0.75rem', color: '#555', letterSpacing: '0.05em',
            fontFamily: '"Copperplate Gothic Light", "Copperplate", "Small Caps", serif',
          }}>
            {firmPhone} - Telephone&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{firmFax} - Facsimile
          </div>
        </div>

        {/* Delivery + Date */}
        <div style={{ fontStyle: 'italic', fontSize: '0.85rem', marginBottom: '0.4rem' }}>
          {delivery}
        </div>
        <div style={{ marginBottom: '1rem' }}>{periodEndFormatted}</div>

        {/* Recipient */}
        <div style={{ marginBottom: '0.75rem' }}>
          {recipient.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>

        <div style={{ marginBottom: '0.25rem' }}>
          <strong>Attention:</strong>&nbsp;&nbsp;&nbsp;{attention}
        </div>
        <div style={{ marginBottom: '1rem' }}>{salutation}</div>

        {/* Subject */}
        <div style={{ marginBottom: '1.25rem' }}>
          <strong>Re:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Payroll for Employees for Period ending {periodEndFormatted}</strong>
        </div>

        {data.employees.length === 0 && (!data.manual_entries || data.manual_entries.length === 0) ? (
          <div style={{ color: '#999', fontStyle: 'italic' }}>
            No employee data for this period.
          </div>
        ) : (
          <>
            {data.employees.map((emp, idx) => (
              <div key={idx} style={{ marginBottom: '1rem' }}>
                <div><strong>{emp.name}{emp.rate_segment ? ` (${emp.rate_segment})` : ''} -</strong></div>
                <div style={{ paddingLeft: '2rem' }}>
                  {buildLine('Regular', emp.combined_regular_hours, emp.rate, emp.combined_regular_pay)}
                </div>
                <div style={{ paddingLeft: '2rem' }}>
                  {buildLine('Overtime', emp.ot_hours, emp.ot_rate, emp.ot_pay)}
                </div>
                {emp.dt_hours !== '0.00' && (
                  <div style={{ paddingLeft: '2rem' }}>
                    {buildLine('Double Time', emp.dt_hours, emp.dt_rate, emp.dt_pay)}
                  </div>
                )}
              </div>
            ))}
            {data.manual_entries?.map((entry) => (
              <div key={`manual-${entry.id}`} style={{ marginBottom: '1rem' }}>
                <strong>{entry.name} - {entry.description} - ${fmtMoney(entry.amount)}</strong>
              </div>
            ))}
          </>
        )}

        {/* Closing */}
        <div style={{ marginTop: '1.5rem' }}>
          {closing}
        </div>
        <div style={{ marginTop: '1.5rem' }}>{signoff}</div>
        {data.signature_data_url ? (
          <div style={{ marginTop: '0.5rem', marginBottom: '0.25rem' }}>
            <img
              src={data.signature_data_url}
              alt="Signature"
              style={{ height: '48px', width: 'auto' }}
            />
          </div>
        ) : (
          <div style={{ marginTop: '2.5rem' }} />
        )}
        <div>
          <div><strong>{firmName}</strong></div>
          <div>{signerName}</div>
        </div>
      </div>
    </div>
  );
};

export default PayrollReport;
